import classnames from 'classnames';
import React, { forwardRef, useRef, useContext } from 'react';
import styled, { ThemeContext, ThemeProvider } from 'styled-components';
import { InputLabelPositions, InputLooks } from '../../../../enums/input.js';
import { SwitchSize } from '../../../../enums/switch.js';
import '../../../../utilities/color.js';
import 'date-fns';
import '../../../../utilities/locale.js';
import { mergeRefs } from '../../../../utilities/refs.js';
import { usePrefersReducedMotion } from '../../../../hooks/use-prefers-reduced-motion.js';
import '../../../../hooks/use-prevent-keyboard-scrolling.js';
import '../../../../styles/styled-components/alert.js';
import '../../../../styles/styled-components/animations.js';
import '../../../../styles/styled-components/box-shadow.js';
import '../../../../styles/styled-components/button.js';
import '../../../../themes/alert.js';
import '../../../../themes/button.js';
import '../../../../themes/checkbox.js';
import '../../../../themes/divider.js';
import '../../../../themes/dropdown.js';
import '../../../../themes/fieldset.js';
import { fontFamilyBook } from '../../../../themes/fonts.js';
import '../../../../themes/input.js';
import '../../../../themes/list.js';
import '../../../../themes/listbox.js';
import '../../../../themes/loader.js';
import '../../../../themes/option.js';
import '../../../../themes/popout.js';
import '../../../../themes/switch.js';
import { lightTheme } from '../../../../themes/themes.js';
import '../../../../themes/tooltip.js';
import '../../../../styles/styled-components/font-face.js';
import '../../../../styles/styled-components/global.js';
import '../../../../styles/styled-components/input.js';
import '../../../../styles/styled-components/list.js';
import '../../../../styles/styled-components/listitem.js';
import '../../../../styles/styled-components/media-queries.js';
import '../../../../styles/styled-components/reset.js';
import '../../../../styles/styled-components/outline.js';
import '../../../../styles/styled-components/popout.js';
import { screenReaderOnly } from '../../../../styles/styled-components/screen-reader.js';
import { getSwitchBase, getSwitchUnchecked, getSwitchChecked, getSwitchOutline, getSwitchDisabled, switchSmall, switchSmallOff, switchSmallLabelLeft, switchSmallLabelRight, switchMedium, switchMediumOff, switchMediumLabelLeft, switchMediumLabelRight, switchReducedMotion } from '../../../../styles/styled-components/switch.js';
import '../../../../styles/styled-components/tooltip.js';
import '../../../atoms/icon/font-icon/font-icon.js';
import 'react-transition-group';
import '../../../atoms/alert/inline-alert/inline-alert.js';
import '../../../atoms/alert/standard-alert/standard-alert.js';
import 'boring-avatars';
import '../../../atoms/tooltip/hover-tooltip/hover-tooltip.js';
import 'react-laag';
import '@brandfolder/utilities';
import '../../../atoms/button/close-button/close-button.js';
import '../../../atoms/button/color-button/color-button.js';
import '../../../atoms/button/icon-button/icon-button.js';
import '../../../atoms/button/standard-button/standard-button.js';
import '../../../atoms/loader/circle-loader/circle-loader.js';
import '../../../atoms/loader/shimmer-loader/shimmer-loader.js';
import '../../../atoms/divider/standard-divider/standard-divider.js';
import '../../../atoms/fieldset/standard-fieldset/standard-fieldset.js';
import '../../../atoms/heading/standard-heading/standard-heading.js';
import { StandardLabel } from '../../../atoms/label/standard-label/standard-label.js';
import '../../../atoms/link/button-link/button-link.js';
import '../../../atoms/link/standard-link/standard-link.js';
import '../../../atoms/popout/standard-popout/standard-popout.js';
import '../../../atoms/screen-reader/only-for-screen-reader/only-for-screen-reader.js';
import '../../../atoms/table/standard-table/caption.js';
import '../../../atoms/table/standard-table/tbody.js';
import '../../../atoms/table/standard-table/tr-empty.js';
import '../../../atoms/table/standard-table/td.js';
import '../../../atoms/table/standard-table/tr.js';
import '../../../atoms/table/standard-table/table.js';
import '../../../atoms/table/standard-table/tfoot.js';
import '../../../atoms/table/standard-table/th.js';
import '../../../atoms/table/standard-table/thead.js';
import '../../../atoms/table/dnd-table/dnd-handle-td.js';
import '../../../atoms/table/dnd-table/dnd-table-drag-context.js';
import { HelpText } from '../../../atoms/text/help-text/help-text.js';
import '../../../atoms/text/standard-text/standard-text.js';
import '../../../internal/list/standard-list/standard-list.js';
import '../../../internal/listbox/standard-listbox/standard-listbox.js';
import '../../../internal/listitem/standard-listitem/standard-listitem.js';
import '../../../internal/option/standard-option/standard-option.js';
import '../../../internal/textarea/fake-textarea.js';
import '../../../internal/textfield/textfield-addon.js';
import '../../../internal/textfield/textfield-body.js';
import '../../../internal/textfield/textfield-header.js';
import '../../../internal/textfield/textfield-icon.js';
import '../../../internal/textfield/textfield-inner.js';
import { TextfieldWrapper } from '../../../internal/textfield/textfield-wrapper.js';
import { BaseCheckbox } from '../../checkbox/base-checkbox/base-checkbox.js';
import '../../checkbox/standard-checkbox/standard-checkbox.js';

const UnstyledCheckboxSwitch = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { defaultChecked, disabled, helpText, helpTextProps, id, inputProps, label, labelPosition, labelProps, name, onBlur, onChange, showLabel, size, switchProps, textProps, wrapperRef, ...otherProps } = props;
    const hasHelpText = !!helpText;
    const helpTextId = `${id}-help-text`;
    const labelId = `${id}-label`;
    const inputRef = useRef(null);
    const prefersReducedMotion = usePrefersReducedMotion();
    return /*#__PURE__*/ React.createElement(TextfieldWrapper, {
        ...otherProps,
        className: classnames({
            'bf-switch': true,
            'bf-switch__checkbox': true,
            'bf-switch__checkbox--disabled': disabled,
            'bf-switch__checkbox--help-text': hasHelpText,
            [`bf-switch__checkbox--label-${labelPosition}`]: showLabel,
            'bf-switch__checkbox--label-hidden': !showLabel,
            [`bf-switch__checkbox--prefers-reduced-motion`]: prefersReducedMotion,
            [`bf-switch__checkbox--size-${size}`]: size,
            [`${otherProps?.className}`]: !!otherProps?.className
        }),
        labelPosition: labelPosition,
        ref: wrapperRef
    }, /*#__PURE__*/ React.createElement(BaseCheckbox, {
        ...inputProps,
        "aria-describedby": hasHelpText ? helpTextId : undefined,
        "aria-disabled": disabled,
        className: classnames({
            'bf-switch__input': true,
            [`${inputProps?.className}`]: !!inputProps?.className
        }),
        defaultChecked: defaultChecked,
        disabled: disabled,
        id: id,
        name: name,
        onBlur: onBlur,
        onChange: onChange,
        onKeyUp: (e)=>{
            if (e.key === 'Enter') inputRef?.current?.click();
            if (inputProps?.onKeyUp) inputProps.onKeyUp(e);
        },
        ref: mergeRefs([
            ref,
            inputRef
        ]),
        role: "switch"
    }), /*#__PURE__*/ React.createElement(StandardLabel, {
        ...labelProps,
        className: classnames({
            'bf-switch__label': true,
            [`${labelProps?.className}`]: !!labelProps?.className
        }),
        htmlFor: id,
        id: labelId
    }, /*#__PURE__*/ React.createElement("span", {
        ...switchProps,
        "aria-hidden": true,
        className: classnames({
            'bf-switch__switch': true,
            [`${switchProps?.className}`]: !!switchProps?.className
        })
    }), /*#__PURE__*/ React.createElement("span", {
        ...textProps,
        className: classnames({
            'bf-switch__text': true,
            [`${textProps?.className}`]: !!textProps?.className
        })
    }, label)), hasHelpText && /*#__PURE__*/ React.createElement(HelpText, {
        ...helpTextProps,
        id: helpTextId
    }, helpText));
});
const Switch = styled(UnstyledCheckboxSwitch)`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  position: relative;

  .bf-switch__label {
    align-items: center;
    cursor: pointer;
    font-family: ${fontFamilyBook};
    font-weight: 400;
  }

  .bf-switch__input {
    ${screenReaderOnly}

    + .bf-switch__label {
      .bf-switch__switch {
        ${()=>getSwitchBase()}
        ${({ look })=>getSwitchUnchecked({
        look
    })}
      }
    }

    &:checked {
      + .bf-switch__label {
        .bf-switch__switch {
          ${({ look })=>getSwitchChecked({
        look
    })}
        }
      }
    }

    &:focus-visible {
      + .bf-switch__label {
        .bf-switch__switch {
          ${({ look })=>getSwitchOutline({
        look
    })}
        }
      }
    }

    &:disabled {
      + .bf-switch__label {
        cursor: not-allowed;

        .bf-switch__switch {
          ${({ look })=>getSwitchDisabled({
        look
    })}
        }
      }
    }
  }

  &.bf-switch__checkbox--label-hidden {
    .bf-switch__text {
      ${screenReaderOnly}
    }

    .bf-text__help {
      ${screenReaderOnly}
      margin-left: 0;
    }
  }

  &.bf-switch__checkbox--size-small {
    .bf-switch__input {
      + .bf-switch__label {
        .bf-switch__switch {
          ${switchSmall}

          &:after {
            ${switchSmallOff}
          }
        }
      }

      &:checked {
        + .bf-switch__label {
          .bf-switch__switch {
            &:after {
              right: 0;
            }
          }
        }
      }
    }

    &.bf-textfield__wrapper--label-position-left {
      ${switchSmallLabelLeft}
    }

    &.bf-textfield__wrapper--label-position-right {
      ${switchSmallLabelRight}
    }
  }

  &.bf-switch__checkbox--size-medium {
    .bf-switch__input {
      + .bf-switch__label {
        .bf-switch__switch {
          ${switchMedium}

          &:after {
            ${switchMediumOff}
          }
        }
      }

      &:checked {
        + .bf-switch__label {
          .bf-switch__switch {
            &:after {
              right: 0;
            }
          }
        }
      }
    }

    &.bf-textfield__wrapper--label-position-left {
      ${switchMediumLabelLeft}
    }

    &.bf-textfield__wrapper--label-position-right {
      ${switchMediumLabelRight}
    }
  }

  &.bf-textfield__wrapper--label-position-left {
    align-items: flex-end;

    .bf-switch__switch {
      order: 1;
    }
  }

  &.bf-switch__checkbox--prefers-reduced-motion {
    .bf-switch__input {
      + .bf-switch__label {
        .bf-switch__switch {
          ${switchReducedMotion}
        }
      }
    }
  }
`;
/**
 * A `CheckboxSwitch` represents on/off values using `<input role="switch" type="checkbox" />` for compatibility with forms.
 *
 * This component differs from `ImmediateSwitch` in that `CheckboxSwitch` is meant to be used within a `<form>`,
 * whereas `ImmediateSwitch` is not. An example implementation using this component with a `<form>`
 * via `react-hook-form` is in the companion Examples story.
 *
 * Required attributes for `CheckboxSwitch` are `id` and `label`.
 * Common optional attributes include `defaultChecked`, `disabled`, and `helpText`.
 * You may hide the label visually (but still be accessible to screen readers) by passing `showLabel={false}`,
 * though this is not recommended.
 *
 * `CheckboxSwitch` sizing defaults to `SwitchSize.Small` (16px circle),
 * with a `SwitchSize.Medium` available (24px circle).
 *
 * Labels may be placed on the right hand side via `InputLabelPositions.Right` (default)
 * or on the left via `InputLabelPositions.Left`.
 *
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/switch_role
 *
 * https://www.w3.org/WAI/ARIA/apg/patterns/switch/examples/switch-checkbox/
 */ const CheckboxSwitch = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { defaultChecked = false, disabled = false, labelPosition = InputLabelPositions.Right, look = InputLooks.Default, showLabel = true, size = SwitchSize.Small, ...otherProps } = props;
    const themeContext = useContext(ThemeContext);
    return /*#__PURE__*/ React.createElement(ThemeProvider, {
        theme: themeContext || lightTheme
    }, /*#__PURE__*/ React.createElement(Switch, {
        ...otherProps,
        defaultChecked: defaultChecked,
        disabled: disabled,
        labelPosition: labelPosition,
        look: look,
        ref: ref,
        showLabel: showLabel,
        size: size
    }));
});

export { CheckboxSwitch, UnstyledCheckboxSwitch };
//# sourceMappingURL=checkbox-switch.js.map
